// Search Modal Component
// Premium search with instant results, suggestions, filters

import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Box,
  Typography,
  IconButton,
  Divider,
  InputAdornment,
  CircularProgress,
  styled,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Search as SearchIcon, Close as CloseIcon, TrendingUp, History, FilterList, X } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { searchAnime, fetchTrending, fetchPopular } from '../../hooks/useApi';
import { Anime } from '../../hooks/useApi';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button';

const useStyles = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  return { isMobile };
};

// Styled Components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': {
    padding: 0,
    [theme.breakpoints.down('sm')]: {
      padding: 0,
      alignItems: 'flex-end',
    },
  },
  '& .MuiDialog-paper': {
    margin: 0,
    borderRadius: theme.breakpoints.up('sm') ? 20 : '20px 20px 0 0',
    maxWidth: 'none',
    width: theme.breakpoints.up('sm') ? 720 : '100%',
    maxHeight: theme.breakpoints.up('sm') ? '85vh' : '70vh',
    boxShadow: theme.shadows[20],
    backgroundColor: 'var(--anime-token-background-paper)',
    border: '1px solid var(--anime-token-divider)',
    [theme.breakpoints.down('sm')]: {
      borderRadius: '20px 20px 0 0',
      maxHeight: '85vh',
    },
  },
}));

const SearchInput = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 16,
    backgroundColor: 'var(--anime-token-surface-default)',
    '& fieldset': {
      borderColor: 'transparent',
    },
    '&:hover fieldset': {
      borderColor: 'transparent',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'var(--anime-token-brand-primary-main)',
      borderWidth: 2,
    },
    '& input': {
      padding: '16px 16px 16px 56px',
      fontSize: '1.1rem',
    },
  },
  '& .MuiInputAdornment-root': {
    position: 'absolute',
    left: 16,
    color: 'var(--anime-token-text-tertiary)',
  },
  '& .MuiInputLabel-root': {
    display: 'none',
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid var(--anime-token-divider)`,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '0.875rem',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  color: 'var(--anime-token-text-tertiary)',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
}));

const ResultItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'highlighted',
})<{ highlighted?: boolean }>(({ theme, highlighted }) => ({
  padding: theme.spacing(1.5, 2),
  borderRadius: 12,
  margin: `${theme.spacing(0.5)} ${theme.spacing(1.5)}`,
  transition: 'all 0.15s ease',
  backgroundColor: highlighted ? 'var(--anime-token-brand-primary-500_10)' : 'transparent',
  '&:hover': {
    backgroundColor: 'var(--anime-token-surface-hover)',
  },
  '& .MuiListItemAvatar-root': {
    minWidth: 56,
  },
}));

const ResultImage = styled('img')(({ theme }) => ({
  width: 48,
  height: 72,
  objectFit: 'cover',
  borderRadius: 8,
  flexShrink: 0,
}));

const ResultMeta = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  marginTop: 4,
  fontSize: '0.75rem',
  color: 'var(--anime-token-text-tertiary)',
}));

const ResultBadge = styled(Chip)(({ theme }) => ({
  fontSize: '0.65rem',
  height: 18,
  borderRadius: 6,
}));

const SuggestionChip = styled(Chip)(({ theme }) => ({
  fontSize: '0.8rem',
  height: 32,
  borderRadius: 16,
  backgroundColor: 'var(--anime-token-surface-default)',
  border: '1px solid var(--anime-token-border-default)',
  color: 'var(--anime-token-text-secondary)',
  fontWeight: 500,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'var(--anime-token-brand-primary-500_15)',
    borderColor: 'var(--anime-token-brand-primary-main)',
    color: 'var(--anime-token-brand-primary-main)',
  },
}));

const FilterChip = styled(Chip)(({ theme }) => ({
  fontSize: '0.75rem',
  height: 28,
  borderRadius: 14,
  backgroundColor: 'var(--anime-token-surface-default)',
  border: '1px solid var(--anime-token-border-default)',
  color: 'var(--anime-token-text-secondary)',
  fontWeight: 500,
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(6),
  color: 'var(--anime-token-text-tertiary)',
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(6),
  textAlign: 'center',
  color: 'var(--anime-token-text-tertiary)',
  gap: theme.spacing(2),
}));

interface SearchModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (id: number) => void;
}

export const SearchModal = ({ open, onClose, onSelect }: SearchModalProps) => {
  const { isMobile } = useStyles();
  const navigate = useNavigate();
  const [query, setQuery] = React.useState('');
  const [results, setResults] = React.useState<Anime[]>([]);
  const [recentSearches, setRecentSearches] = React.useState<string[]>([]);
  const [trendingSearches, setTrendingSearches] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState(-1);
  const [showFilters, setShowFilters] = React.useState(false);
  const [filters, setFilters] = React.useState({
    genre: '',
    type: '',
    year: '',
    status: '',
    sort: 'relevance',
  });
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Load recent searches from localStorage
  React.useEffect(() => {
    const stored = localStorage.getItem('recentSearches');
    if (stored) {
      try {
        setRecentSearches(JSON.parse(stored));
      } catch {
        setRecentSearches([]);
      }
    }
  }, []);

  // Load trending searches
  React.useEffect(() => {
    const loadTrending = async () => {
      try {
        const trending = await fetchTrending(1, 10);
        setTrendingSearches(trending.map(a => a.title).slice(0, 8));
      } catch (e) {
        console.warn('Failed to load trending searches', e);
      }
    };
    loadTrending();
  }, []);

  // Save recent search
  const saveRecentSearch = (search: string) => {
    const updated = [search, ...recentSearches.filter(s => s !== search)].slice(0, 10);
    setRecentSearches(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
  };

  const handleSearch = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setSelectedIndex(-1);
    try {
      const items = await searchAnime(query.trim());
      setResults(items);
      saveRecentSearch(query.trim());
    } catch (error) {
      console.error('Search failed', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (anime: Anime) => {
    onSelect(anime.id);
    saveRecentSearch(query.trim());
    onClose();
    setQuery('');
    setResults([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      handleSelect(results[selectedIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    handleSearch();
  };

  const handleRecentClick = (recent: string) => {
    setQuery(recent);
    handleSearch();
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recentSearches');
  };

  const removeRecentSearch = (search: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setRecentSearches(prev => prev.filter(s => s !== search));
    localStorage.setItem('recentSearches', JSON.stringify(recentSearches.filter(s => s !== search)));
  };

  // Focus input on open
  React.useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // Reset on close
  React.useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
      setSelectedIndex(-1);
    }
  }, [open]);

  if (!open) return null;

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      BackdropProps={{
        timeout: 200,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Search Header */}
        <Box
          sx={{
            padding: isMobile ? 3 : 4,
            borderBottom: '1px solid var(--anime-token-divider)',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <IconButton
            onClick={onClose}
            aria-label="Close search"
            sx={{ color: 'var(--anime-token-text-secondary)', mr: -1 }}
          >
            <CloseIcon fontSize="large" />
          </IconButton>

          <SearchInput
            ref={inputRef}
            fullWidth
            placeholder="Search anime, movies, characters..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(-1);
              if (e.target.value.trim()) {
                handleSearch();
              } else {
                setResults([]);
              }
            }}
            onKeyDown={handleKeyDown}
            onSubmit={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'var(--anime-token-text-tertiary)' }} />
                </InputAdornment>
              ),
              endAdornment: query && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setQuery('')}
                    aria-label="Clear search"
                    size="small"
                  >
                    <X fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            autoComplete="off"
            autoFocus
          />

          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
              <Button
                variant="ghost"
                size="small"
                startIcon={<FilterList fontSize="small" />}
                onClick={() => setShowFilters(!showFilters)}
              >
                Filters
              </Button>
            </Box>
          )}
        </Box>

        {/* Filters Panel */}
        <AnimatePresence>
          {showFilters && !isMobile && (
            <Box
              sx={{
                padding: 3,
                borderBottom: '1px solid var(--anime-token-divider)',
                backgroundColor: 'var(--anime-token-surface-variant)',
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 1, fontWeight: 600 }}>
                  Filters:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <FilterChip
                    label="TV"
                    onClick={() => setFilters(prev => ({ ...prev, type: prev.type === 'TV' ? '' : 'TV' }))}
                    variant={filters.type === 'TV' ? 'filled' : 'outlined'}
                    color={filters.type === 'TV' ? 'primary' : 'default'}
                  />
                  <FilterChip
                    label="Movie"
                    onClick={() => setFilters(prev => ({ ...prev, type: prev.type === 'Movie' ? '' : 'Movie' }))}
                    variant={filters.type === 'Movie' ? 'filled' : 'outlined'}
                    color={filters.type === 'Movie' ? 'primary' : 'default'}
                  />
                  <FilterChip
                    label="OVA"
                    onClick={() => setFilters(prev => ({ ...prev, type: prev.type === 'OVA' ? '' : 'OVA' }))}
                    variant={filters.type === 'OVA' ? 'filled' : 'outlined'}
                    color={filters.type === 'OVA' ? 'primary' : 'default'}
                  />
                  <FilterChip
                    label="ONA"
                    onClick={() => setFilters(prev => ({ ...prev, type: prev.type === 'ONA' ? '' : 'ONA' }))}
                    variant={filters.type === 'ONA' ? 'filled' : 'outlined'}
                    color={filters.type === 'ONA' ? 'primary' : 'default'}
                  />
                  <FilterChip
                    label="Airing"
                    onClick={() => setFilters(prev => ({ ...prev, status: prev.status === 'RELEASING' ? '' : 'RELEASING' }))}
                    variant={filters.status === 'RELEASING' ? 'filled' : 'outlined'}
                    color={filters.status === 'RELEASING' ? 'success' : 'default'}
                  />
                  <FilterChip
                    label="Finished"
                    onClick={() => setFilters(prev => ({ ...prev, status: prev.status === 'FINISHED' ? '' : 'FINISHED' }))}
                    variant={filters.status === 'FINISHED' ? 'filled' : 'outlined'}
                    color={filters.status === 'FINISHED' ? 'default' : 'default'}
                  />
                  <FilterChip
                    label="2024"
                    onClick={() => setFilters(prev => ({ ...prev, year: prev.year === '2024' ? '' : '2024' }))}
                    variant={filters.year === '2024' ? 'filled' : 'outlined'}
                  />
                  <FilterChip
                    label="2023"
                    onClick={() => setFilters(prev => ({ ...prev, year: prev.year === '2023' ? '' : '2023' }))}
                    variant={filters.year === '2023' ? 'filled' : 'outlined'}
                  />
                  {Object.values(filters).some(v => v) && (
                    <FilterChip
                      label="Clear All"
                      onClick={() => setFilters({ genre: '', type: '', year: '', status: '', sort: 'relevance' })}
                      variant="outlined"
                      color="error"
                    />
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </AnimatePresence>

        {/* Results */}
        <DialogContent sx={{ flex: 1, overflow: 'auto', padding: 0 }}>
          {loading && results.length === 0 && (
            <LoadingContainer>
              <CircularProgress size={32} color="primary" />
              <Typography variant="body2" sx={{ ml: 2 }}>Searching...</Typography>
            </LoadingContainer>
          )}

          {!loading && query && results.length === 0 && (
            <EmptyState>
              <SearchIcon sx={{ fontSize: 48, opacity: 0.5 }} />
              <Typography variant="h6" color="text.secondary">
                No results found
              </Typography>
              <Typography variant="body2" color="text.tertiary" sx={{ maxWidth: 300 }}>
                Try different keywords or check your spelling
              </Typography>
            </EmptyState>
          )}

          {!loading && results.length > 0 && (
            <List dense disablePadding>
              {results.map((anime, index) => (
                <ResultItem
                  key={anime.id}
                  highlighted={index === selectedIndex}
                  onClick={() => handleSelect(anime)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <ListItemAvatar>
                    <ResultImage
                      src={anime.poster}
                      alt={anime.title}
                      loading="lazy"
                      onError={(e) => { e.currentTarget.src = '/images/placeholder-poster.jpg'; }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={anime.title}
                    secondary={
                      <ResultMeta>
                        <span>{anime.type}</span>
                        <span>•</span>
                        <span>{anime.episodes} eps</span>
                        <span>•</span>
                        <span style={{ color: '#FBBF24', fontWeight: 600 }}>★ {anime.score.toFixed(1)}</span>
                        {anime.year && <><span>•</span><span>{anime.year}</span></>}
                        {anime.status && <><span>•</span><span>{anime.status}</span></>}
                      </ResultMeta>
                    }
                  />
                </ResultItem>
              ))}
            </List>
          )}

          {!query && !loading && (
            <Box sx={{ padding: 3 }}>
              {/* Recent Searches */}
              {recentSearches.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <SectionHeader>
                    <SectionTitle startIcon={<History fontSize="small" />}>Recent Searches</SectionTitle>
                    <IconButton size="small" onClick={clearRecentSearches} aria-label="Clear recent searches">
                      <Typography variant="caption" color="text.secondary">Clear all</Typography>
                    </IconButton>
                  </SectionHeader>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, px: 1 }}>
                    {recentSearches.map((search) => (
                      <SuggestionChip
                        key={search}
                        label={search}
                        onClick={() => handleRecentClick(search)}
                        onDelete={() => removeRecentSearch(search)}
                        deleteIcon={<X fontSize="small" />}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Trending Searches */}
              {trendingSearches.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <SectionHeader>
                    <SectionTitle startIcon={<TrendingUp fontSize="small" />}>Trending Searches</SectionTitle>
                  </SectionHeader>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, px: 1 }}>
                    {trendingSearches.map((search) => (
                      <SuggestionChip
                        key={search}
                        label={search}
                        onClick={() => handleSuggestionClick(search)}
                        startIcon={<TrendingUp fontSize="small" style={{ color: '#FBBF24' }} />}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Popular Categories */}
              <Box>
                <SectionHeader>
                  <SectionTitle>Browse Categories</SectionTitle>
                </SectionHeader>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 2, px: 1 }}>
                  {[
                    { label: 'Action', genre: 'Action' },
                    { label: 'Adventure', genre: 'Adventure' },
                    { label: 'Comedy', genre: 'Comedy' },
                    { label: 'Drama', genre: 'Drama' },
                    { label: 'Fantasy', genre: 'Fantasy' },
                    { label: 'Sci-Fi', genre: 'Sci-Fi' },
                    { label: 'Romance', genre: 'Romance' },
                    { label: 'Slice of Life', genre: 'Slice of Life' },
                  ].map((cat) => (
                    <Button
                      key={cat.label}
                      variant="soft"
                      fullWidth
                      onClick={() => handleSuggestionClick(cat.genre)}
                      sx={{
                        py: 2,
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        borderRadius: 2,
                      }}
                    >
                      {cat.label}
                    </Button>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Box>
    </StyledDialog>
  );
};

export default SearchModal;